/**
 * `client-info-requests` (crossmarc/steward-app#2280, "Information requests 2") — the firm's
 * record of what it is asking ONE client for: advisor-added accounts the snapshot does not list,
 * the requests themselves (a statement for an account, a document, a question), and the sends
 * (the shape is declared and bounded here; the send route of #2283 writes them). One row per client, in the
 * firm's own workspace.
 *
 * ## The client reads this row through appcore, and appcore hands it over unfiltered
 *
 * appcore's onboarding form read (`readRequestsRow`, `onboarding-form-repository.ts`, crossmarc/appcore#826,
 * read at appcore origin/main 8aae98f on 2026-10-09) finds the row from the client's id alone and
 * returns EVERY stored attribute under `requests` on the form read. The façade's own
 * `GET /me/onboarding-forms/{firmWorkspaceId}` (`handlers/onboardingForm.ts`) is what the client's
 * browser calls, and it does NOT relay that value: it relays {@link projectInfoRequestsForClient}
 * of it (and {@link latestSendNoteForClient}, one note as text), which keeps only requests that
 * were sent and not withdrawn, with no staff name, no account list, no send and no draft (plus
 * {@link projectWithdrawnInfoRequestsForClient}: what was asked, for a withdrawn request the client
 * had added to). What makes that true is the projection, pinned by
 * `clientInfoRequests.test.ts` (the shape, in both directions) and by the façade's
 * `onboardingForm.test.ts` (the wire), not this paragraph. What appcore's read relies on, and what
 * the type declaration in `index.ts` therefore has to say (pinned in `clientInfoRequests.test.ts`,
 * because appcore enforces none of it on that path):
 *
 *   - type code `client-info-requests` (`ONBOARDING_REQUESTS_TYPE_CODE`);
 *   - a `clientId` field, a string, required and `unique: true` (without `unique`, a second row
 *     can hide this client's requests);
 *   - `accessPatterns: [{ name: 'by_clientId', fields: ['clientId'] }]` (`ONBOARDING_REQUESTS_LOOKUP_PATTERN`);
 *   - no `sensitive` field and not `publishable`: that path does not strip `sensitive` fields,
 *     and a `publishable` type's new rows rest at `draft`, which the read refuses;
 *   - the row re-checked on `attributes.clientId`, which this row's schema always carries.
 *
 * ## Why the type is `schemaless`, and the row schema is the validator
 *
 * The brief for this slice and the issue's second comment say "strict". A strict type cannot hold
 * this row: appcore compiles a strict `array` field to `z.array(z.string())` (`zod-compiler.ts`
 * `case 'array'`, read at appcore origin/main 8aae98f on 2026-10-09), and `accounts`, `requests`
 * and `sends` are arrays of objects, so every write would 400 (#1859, `packTypeDefs.test.ts`).
 * `client-steward-setup` is the precedent for the other route: `schemaMode: 'schemaless'`, the
 * façade's Zod schema as the validator. `unique` and `accessPatterns` do not depend on
 * `schemaMode` (the uniqueness slots and lookup items are written by the entity repository from
 * the declared fields). The cost, stated plainly: appcore then enforces NOTHING about this row's
 * content, and it does not enforce `required` either, so a row written without `clientId` gets
 * no unique slot and no lookup item (it would be invisible to the client's read and would not
 * hold the slot). {@link ClientInfoRequestsAttributesSchema} is the ONLY shape control, strict at
 * every level, and it is only as good as the writers that call it: the façade parses it on every
 * read and every write, and a write by any other route would not be checked.
 *
 * ## The row is built as if it could be shown to the client
 *
 * appcore's read hands the whole stored row on, so the schema is `strictObject` at every level
 * and carries display names only: no user id, no email, no firm id. The only identifiers are the
 * request id (the grammar appcore's client upload routes accept for a `requestId`,
 * {@link INFO_REQUEST_ID_PATTERN}), the account key (a digest, see `clientAccountNumberMark.ts`)
 * and `clientId`, which the client already holds. Free text goes through {@link cleanInfoText}
 * (`safeDisplayText`, zero-width characters, whitespace, and the digits of any run of nine or
 * more hidden as `#`), and the stored value must be a fixed point of it, so a row written by
 * anything else does not parse. That is a second line of defence: what the client's browser
 * receives is the projection above, not the row.
 *
 * ## Size
 *
 * Caps, dated 2026-10-09: {@link MAX_INFO_ACCOUNTS} accounts, {@link MAX_INFO_REQUESTS} requests
 * still wanted (not withdrawn), {@link MAX_INFO_REQUEST_HISTORY} requests in all, {@link MAX_INFO_SENDS}
 * sends of at most {@link MAX_SEND_REQUEST_IDS} request ids each, and {@link MAX_INFO_ROW_BYTES}
 * bytes of JSON for the whole attribute bag. DynamoDB's item limit is 400 KB (409,600 bytes).
 * The per-field limits count UTF-16 units, not bytes, and do NOT bound the row: a row with every
 * list at its count cap and every field at its limit is over {@link MAX_INFO_ROW_BYTES} even in
 * ASCII, and far over it in multi-byte text. The byte cap is what bounds the row, it is checked
 * on every write (and by the schema, so an oversized row does not parse), and it is set below the
 * item limit to leave room for DynamoDB's per-attribute overhead and appcore's envelope.
 * `clientInfoRequests.test.ts` builds the maximal row and asserts it is refused.
 *
 * ## Status is derived, never stored
 *
 * {@link deriveInfoRequestStatus}. The row stores `withdrawnAt`; "answered" is a fact about what
 * the client has since provided, which lives elsewhere and changes without this row changing.
 */
import { z } from 'zod';
import { AccountKeySchema, AccountLabelSchema, ACCOUNT_LABEL_MAX, ZERO_WIDTH_CHARS, clipUtf16, looksLikeAccountNumber, redactNumberRuns } from './clientAccountNumberMark.js';
import { safeDisplayText } from './displayText.js';

export const CLIENT_INFO_REQUESTS_TYPE_CODE = 'client-info-requests';

/** The access pattern name appcore's client-side read looks up (`ONBOARDING_REQUESTS_LOOKUP_PATTERN`). */
export const CLIENT_INFO_REQUESTS_LOOKUP_PATTERN = 'by_clientId';

/** The grammar appcore's client upload routes accept for a `requestId` path segment
 *  (crossmarc/appcore#830, merged to origin/main on 2026-10-09 as 997f437: the implementing
 *  session's reading of the LOCAL origin/main with `git log`, no fetch, not re-checked against the
 *  remote). A request id minted here must match it. */
export const INFO_REQUEST_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

/** `account-number` (docs/38, crossmarc/steward-app#2298): the full account number of one account,
 *  and optionally its routing number. The stored schema accepts it, and the staff routes write it:
 *  the draft save, and the route that creates a staff-only one (`handlers/clientInfoRequests.ts`,
 *  docs/38 §8 step 7). What the client's read does with the kind is said at the projection below. */
export const INFO_REQUEST_KINDS = ['statement', 'document', 'question', 'account-number'] as const;
export type InfoRequestKind = (typeof INFO_REQUEST_KINDS)[number];

export const INFO_SEND_KINDS = ['invitation', 'request'] as const;
export type InfoSendKind = (typeof INFO_SEND_KINDS)[number];

/** Advisor-added accounts per client. */
export const MAX_INFO_ACCOUNTS = 50;
/** Requests per client that are still wanted: drafts and sent ones that are not withdrawn. */
export const MAX_INFO_REQUESTS = 150;
/** Requests in the row in all, withdrawn history included. The byte cap normally bites first;
 *  this keeps the array bounded even for very short requests. */
export const MAX_INFO_REQUEST_HISTORY = 1000;
/** Sends per client (`invitation` and `request` sends only: a reminder is not a send entry, see
 *  `lastReminderAt`). */
export const MAX_INFO_SENDS = 40;
/** Request ids one send may name. */
export const MAX_SEND_REQUEST_IDS = 50;
/** The whole attribute bag as JSON, in bytes. See the header. */
export const MAX_INFO_ROW_BYTES = 262_144;

export const INFO_TEXT_MAX = {
  institution: 100, kind: 80, owner: 100, name: 100, prompt: 500, note: 500,
} as const;

/**
 * Display text the way the marks clean it: `safeDisplayText`, then the zero-width characters,
 * then whitespace collapsed and trimmed. Returns '' when nothing is left. The digits of any run
 * of nine or more (see `redactNumberRuns`) are stored as `#`; nothing here is ever refused for
 * its content. Cut to `max` UTF-16 code units (the unit the schema counts) without splitting a
 * surrogate pair, and trimmed again after the cut, so the result is always a fixed point of this
 * function.
 */
export function cleanInfoText(raw: string, max: number): string {
  const collapse = (v: string) => v.replace(/\s+/g, ' ').trim();
  const cleaned = collapse(safeDisplayText(raw).replace(ZERO_WIDTH_CHARS, ''));
  return clipUtf16(collapse(redactNumberRuns(cleaned)), max).trim();
}

function infoText(max: number) {
  return z.string().min(1).max(max)
    .refine((value) => value === cleanInfoText(value, max), 'text must already be cleaned')
    .refine((value) => !looksLikeAccountNumber(value), 'text must not contain an account number');
}

const instant = z.iso.datetime();
const requestId = z.string().regex(INFO_REQUEST_ID_PATTERN);

/** An account the advisor added because the snapshot does not list it. `key` is minted by the
 *  façade (`row:` + digest), `kind` is what sort of account ("Roth IRA"). */
export const InfoRequestAccountSchema = z.strictObject({
  key: AccountKeySchema,
  institution: infoText(INFO_TEXT_MAX.institution),
  kind: infoText(INFO_TEXT_MAX.kind),
  owner: infoText(INFO_TEXT_MAX.owner).optional(),
  addedByName: infoText(INFO_TEXT_MAX.name),
  addedAt: instant,
});
export type InfoRequestAccount = z.infer<typeof InfoRequestAccountSchema>;

export const InfoRequestSchema = z.strictObject({
  id: requestId,
  kind: z.enum(INFO_REQUEST_KINDS),
  accountKey: AccountKeySchema.optional(),
  /** The account's name as the staff member's browser sent it when the request was made. It is
   *  NOT checked against `accountKey` (the façade cannot see the snapshot), so it is a label,
   *  not a fact about the account. Shown when `accountKey` matches no account. */
  accountLabel: AccountLabelSchema.refine((v) => v === cleanInfoText(v, ACCOUNT_LABEL_MAX), 'text must already be cleaned').optional(),
  prompt: infoText(INFO_TEXT_MAX.prompt).optional(),
  /** `account-number` requests only: also ask for the routing number (docs/38 §9 decision 2). */
  wantsRouting: z.boolean().optional(),
  /** `account-number` requests only (docs/38 §4.5, crossmarc/steward-app#2298 slice 4b): the firm
   *  records this number itself and the client is NOT asked for it. See {@link isStaffOnlyInfoRequest}.
   *  Only `true` is storable, so the flag cannot be written as `false`. */
  staffOnly: z.literal(true).optional(),
  /** Set when the request is sent. Absent on a draft; the send route sets it. */
  requestedAt: instant.optional(),
  requestedByName: infoText(INFO_TEXT_MAX.name),
  withdrawnAt: instant.optional(),
  withdrawnByName: infoText(INFO_TEXT_MAX.name).optional(),
}).superRefine((r, ctx) => {
  const need = (ok: boolean, path: string, message: string) => { if (!ok) ctx.addIssue({ code: 'custom', path: [path], message }); };
  need((r.accountKey === undefined) === (r.accountLabel === undefined), 'accountLabel', 'accountKey and accountLabel go together');
  need(r.kind !== 'statement' || r.accountKey !== undefined, 'accountKey', 'a statement request names an account');
  need(r.kind !== 'account-number' || r.accountKey !== undefined, 'accountKey', 'an account-number request names an account');
  need(r.kind === 'statement' || r.kind === 'account-number' || r.prompt !== undefined, 'prompt', 'a document or question request says what is asked');
  need(r.kind !== 'account-number' || r.prompt === undefined, 'prompt', 'an account-number request takes no prompt');
  need(r.kind === 'account-number' || r.wantsRouting === undefined, 'wantsRouting', 'only an account-number request can ask for a routing number');
  need(r.kind === 'account-number' || r.staffOnly === undefined, 'staffOnly', 'only an account-number request can be staff only');
  need(r.staffOnly === undefined || r.requestedAt === undefined, 'requestedAt', 'a staff-only request is never sent');
  need((r.withdrawnAt === undefined) === (r.withdrawnByName === undefined), 'withdrawnByName', 'withdrawnAt and withdrawnByName go together');
});
export type InfoRequest = z.infer<typeof InfoRequestSchema>;

export const InfoSendSchema = z.strictObject({
  at: instant,
  byName: infoText(INFO_TEXT_MAX.name),
  kind: z.enum(INFO_SEND_KINDS),
  note: infoText(INFO_TEXT_MAX.note).optional(),
  requestIds: z.array(requestId).max(MAX_SEND_REQUEST_IDS),
});
export type InfoSend = z.infer<typeof InfoSendSchema>;

function unique<T>(items: readonly T[], by: (item: T) => string): boolean {
  return new Set(items.map(by)).size === items.length;
}

/**
 * The STORED row. The client is shown {@link projectInfoRequestsForClient} of it, not the row.
 * `strictObject` at every level is load-bearing (header): `clientInfoRequests.test.ts` pins the refusal of an extra key at the
 * row, an account, a request and a send, and of a user-id-shaped key at each.
 */
export const ClientInfoRequestsAttributesSchema = z.strictObject({
  /** The `advisor-client` row this belongs to. Appcore's client read re-checks it. */
  clientId: z.string().min(1).max(200),
  accounts: z.array(InfoRequestAccountSchema).max(MAX_INFO_ACCOUNTS),
  requests: z.array(InfoRequestSchema).max(MAX_INFO_REQUEST_HISTORY),
  sends: z.array(InfoSendSchema).max(MAX_INFO_SENDS),
  /** The LAST reminder email, overwritten by each one: when, and by whom (the same name rule as
   *  `sends[].byName`). A reminder is not a `sends` entry, so it can never use up the
   *  {@link MAX_INFO_SENDS} the row holds, and the row keeps no history of reminders (the
   *  mail provider's log and the audit trail are the history). Both or neither. */
  lastReminderAt: instant.optional(),
  lastReminderByName: infoText(INFO_TEXT_MAX.name).optional(),
}).superRefine((row, ctx) => {
  if ((row.lastReminderAt === undefined) !== (row.lastReminderByName === undefined)) {
    ctx.addIssue({ code: 'custom', path: ['lastReminderByName'], message: 'lastReminderAt and lastReminderByName go together' });
  }
  if (!unique(row.accounts, (a) => a.key)) ctx.addIssue({ code: 'custom', path: ['accounts'], message: 'account keys must be unique' });
  if (!unique(row.requests, (r) => r.id)) ctx.addIssue({ code: 'custom', path: ['requests'], message: 'request ids must be unique' });
  if (row.requests.filter((r) => r.withdrawnAt === undefined).length > MAX_INFO_REQUESTS) {
    ctx.addIssue({ code: 'custom', path: ['requests'], message: 'too many requests that are still wanted' });
  }
  if (new TextEncoder().encode(JSON.stringify(row)).length > MAX_INFO_ROW_BYTES) {
    ctx.addIssue({ code: 'custom', message: 'the row is larger than the most that can be kept' });
  }
});
export type ClientInfoRequestsAttributes = z.infer<typeof ClientInfoRequestsAttributesSchema>;

/** The top-level attribute names a stored row may carry. Exported so a test can pin the set. */
export const CLIENT_INFO_REQUESTS_KEYS = ['clientId', 'accounts', 'requests', 'sends', 'lastReminderAt', 'lastReminderByName'] as const;

export function readClientInfoRequests(
  value: unknown,
): { kind: 'row'; row: ClientInfoRequestsAttributes } | { kind: 'unreadable' } {
  const parsed = ClientInfoRequestsAttributesSchema.safeParse(value);
  return parsed.success ? { kind: 'row', row: parsed.data } : { kind: 'unreadable' };
}

/** The row's JSON size in bytes, as the cap measures it. */
export function infoRequestsRowBytes(row: unknown): number {
  return new TextEncoder().encode(JSON.stringify(row)).length;
}

// ── What the client is shown ──────────────────────────────────────────────────────────────

/** One of the client's own uploads under a request, as the client sees it: six fields copied by
 *  name, never a storage key, a URL, a filename other than the sanitised title, or an address. */
export interface ClientUploadForClient {
  id: string;
  title: string;
  mimeType: string;
  fileSize: number;
  createdAt: string;
}

/** One request as the client sees it: sent and not withdrawn, with no staff name. A statement or
 *  document request carries `documents` (the client's own uploads under it) only when there are
 *  some; a question or an account-number request never does. `wantsRouting` is present, as `true`,
 *  only on an account-number request that also asks for the routing number (docs/38 §5). The
 *  masked state of a saved number is NOT here: it comes from appcore's slot list
 *  (`AccountNumberState`, joined by the façade), never from the stored requests row. */
export interface InfoRequestForClient {
  id: string;
  kind: InfoRequestKind;
  accountKey?: string;
  accountLabel?: string;
  prompt?: string;
  wantsRouting?: true;
  requestedAt: string;
  documents?: ClientUploadForClient[];
}

/** Uploads one statement request takes (owner reading of the issue, "one upload per requested
 *  statement", 2026-10-10). The façade's mint and commit relays refuse at or above this count,
 *  but ONLY when appcore's form read carries the caller's `documents` list
 *  (crossmarc/appcore#832, not merged on 2026-10-10). Against an appcore without it the relays
 *  refuse every upload (`openRequestFor`). The check is read-then-write, so two commits that race
 *  can both pass it (see {@link maxUploadsForRequest}). */
export const MAX_UPLOADS_PER_STATEMENT_REQUEST = 1;
/** Uploads one document request takes. A choice made in the client slice (2026-10-10): the
 *  issue says only "uploads for documents". Enforced the same way as the statement figure. */
export const MAX_UPLOADS_PER_DOCUMENT_REQUEST = 10;

/** How many uploads a request of this kind takes; 0 for a question, which takes none. This is the
 *  figure the relays compare against the TRUE stored count. It is a read-then-write check (the
 *  count comes from a form read, the commit is a separate write), so two commits that race can
 *  both pass it. What bounds a race is appcore's per-client limit of 100 uploads and its request
 *  rate limits (read at appcore origin/main 997f437, 2026-10-10), not this function. */
export function maxUploadsForRequest(kind: InfoRequestKind): number {
  return kind === 'statement' ? MAX_UPLOADS_PER_STATEMENT_REQUEST : kind === 'document' ? MAX_UPLOADS_PER_DOCUMENT_REQUEST : 0;
}

/** The longest typed answer to a question request. appcore's cap on one `requests_answerTexts`
 *  item, a copy of {@link ONBOARDING_ARRAY_ITEM_MAX_BY_KEY} (read from appcore origin/main 997f437
 *  on 2026-10-09). */
export const INFO_ANSWER_MAX = 4000;

/** appcore's `ONBOARDING_DOCUMENT_ID_PATTERN`: the ULID its upload mint generates. Copied from
 *  `onboarding-client-document-repository.ts` at appcore origin/main (read 2026-10-10). */
const OWN_UPLOAD_ID_PATTERN = /^[0-9A-HJKMNP-TV-Z]{26}$/;
const OWN_UPLOAD_TITLE_MAX = 200;

/**
 * ONE own upload, checked and copied field by field: a ULID `id`, string `mimeType` (at most 100)
 * and `createdAt` (at most 40), a finite non-negative `fileSize`, and a title (`titleKey`: `title`
 * on the form read, `filename` on appcore's commit response) cleaned the way display text is
 * (`safeDisplayText`, zero-width characters, clipped to 200) and not blank. `requestId`, when
 * present, must be in grammar. `undefined` for anything else. The façade's commit relay uses this
 * on appcore's commit response so a file the client has just added reads exactly as it will on the
 * next form read, and a malformed response is an error there, not a half-filled row.
 */
export function readOwnUpload(
  entry: unknown, titleKey: 'title' | 'filename' = 'title',
): (ClientUploadForClient & { requestId?: string }) | undefined {
  if (typeof entry !== 'object' || entry === null) return undefined;
  const e = entry as Record<string, unknown>;
  const { id, requestId, mimeType, fileSize, createdAt } = e;
  const title = e[titleKey];
  if (typeof id !== 'string' || !OWN_UPLOAD_ID_PATTERN.test(id)) return undefined;
  if (requestId !== undefined && (typeof requestId !== 'string' || !INFO_REQUEST_ID_PATTERN.test(requestId))) return undefined;
  if (typeof title !== 'string' || typeof mimeType !== 'string' || typeof createdAt !== 'string') return undefined;
  if (typeof fileSize !== 'number' || !Number.isFinite(fileSize) || fileSize < 0) return undefined;
  const cleanTitle = clipUtf16(safeDisplayText(title).replace(ZERO_WIDTH_CHARS, '').trim(), OWN_UPLOAD_TITLE_MAX).trim();
  if (cleanTitle === '' || mimeType.length > 100 || createdAt.length > 40) return undefined;
  return { id, title: cleanTitle, mimeType, fileSize, createdAt, ...(typeof requestId === 'string' ? { requestId } : {}) };
}

/**
 * The caller's own uploads from appcore's form read (`documents`, crossmarc/appcore#832, which was
 * not merged when this was written, 2026-10-10), keyed by the `requestId` each was filed under.
 * appcore checks a `requestId`'s grammar only, so the keys here are NOT yet trusted:
 * {@link projectInfoRequestsForClient} keeps a group only for a request in the projection. Each
 * entry goes through {@link readOwnUpload} and must carry a `requestId`; one that does not is
 * skipped, as is a repeated id. Anything else in `raw` is ignored.
 */
function groupOwnUploads(raw: unknown): Map<string, ClientUploadForClient[]> {
  const out = new Map<string, ClientUploadForClient[]>();
  if (!Array.isArray(raw)) return out;
  const seen = new Set<string>();
  for (const entry of raw) {
    const up = readOwnUpload(entry);
    if (up === undefined || up.requestId === undefined || seen.has(up.id)) continue;
    seen.add(up.id);
    const { requestId, ...shown } = up;
    const list = out.get(requestId) ?? [];
    list.push(shown);
    out.set(requestId, list);
  }
  return out;
}

/**
 * The only part of the stored row the client's browser receives (`GET /me/onboarding-forms/...`
 * in `handlers/onboardingForm.ts` relays this and nothing else of appcore's `requests` value).
 * `raw` is parsed strictly; a row that does not parse yields `undefined` (the caller logs that,
 * without the contents). A parsed row yields ONLY requests that were sent (`requestedAt`) and not
 * withdrawn, each rebuilt field by field, so there is no staff name, no account list, no send, no
 * draft, no withdrawn entry and no key this function did not name.
 *
 * `ownDocuments` is appcore's list of the caller's own uploads (see {@link groupOwnUploads}). They
 * are attached to the request they were filed under, and an upload whose `requestId` names no
 * request in THIS projection (a draft, a withdrawn request, an id nobody sent) is dropped, as is
 * any upload under a question request, which takes none. EVERY other own upload is shown, in the
 * order appcore listed them, even past {@link maxUploadsForRequest}: the client must always be
 * able to see and remove what is stored. The cap is applied by the upload relays to this true
 * count, not here.
 */
export function projectInfoRequestsForClient(raw: unknown, ownDocuments?: unknown): InfoRequestForClient[] | undefined {
  const read = readClientInfoRequests(raw);
  if (read.kind !== 'row') return undefined;
  const uploads = groupOwnUploads(ownDocuments);
  const out: InfoRequestForClient[] = [];
  for (const r of read.row.requests) {
    if (r.requestedAt === undefined || r.withdrawnAt !== undefined) continue;
    // A kind that takes no file never carries one, so an upload filed under such an id (appcore
    // checks a requestId's grammar only) is not attached.
    const documents = maxUploadsForRequest(r.kind) === 0 ? [] : (uploads.get(r.id) ?? []);
    out.push({
      id: r.id,
      kind: r.kind,
      ...(r.accountKey === undefined ? {} : { accountKey: r.accountKey }),
      ...(r.accountLabel === undefined ? {} : { accountLabel: r.accountLabel }),
      ...(r.prompt === undefined ? {} : { prompt: r.prompt }),
      ...(r.kind === 'account-number' && r.wantsRouting === true ? { wantsRouting: true as const } : {}),
      requestedAt: r.requestedAt,
      ...(documents.length === 0 ? {} : { documents }),
    });
  }
  return out;
}

/** A request the firm has withdrawn that holds something of the client's own, as the client
 *  sees it: what was asked, and nothing else. "Holds" is read when the form is read, with no
 *  comparison of times: an answer saved under a question after it was withdrawn (the façade's
 *  section save checks an answer id's grammar only) lists it too. No staff name, no withdrawal time, no account key,
 *  no file list and no answer text (crossmarc/steward-app#2302). */
export interface WithdrawnInfoRequestForClient {
  id: string;
  kind: InfoRequestKind;
  accountLabel?: string;
  prompt?: string;
}

/**
 * The withdrawn requests the client is still shown (crossmarc/steward-app#2302, founder decision
 * 2026-10-10): sent, since withdrawn, and holding something of the client's own. "Holding" is at
 * least one of the caller's uploads filed under it for a statement or document request
 * (`ownDocuments`, as in {@link projectInfoRequestsForClient}), or a non-blank typed answer for a
 * question (`detailsAttributes`, read by {@link answersForRequests}). A withdrawn request with
 * neither is not returned (an account-number request never holds either: see the comment at
 * `holds` below), and neither is a draft or a request that is still wanted, so this list
 * and {@link projectInfoRequestsForClient}'s never share an id. `undefined` for a row that does
 * not parse.
 *
 * Kept out of the `requests` projection on purpose: everything that reads that list (the upload
 * relays' `openRequestFor`, the note rule, the client's counts) takes "in the list" to mean
 * "still wanted", and a withdrawn request must stay closed to new uploads.
 */
export function projectWithdrawnInfoRequestsForClient(
  raw: unknown, ownDocuments: unknown, detailsAttributes: unknown,
): WithdrawnInfoRequestForClient[] | undefined {
  const read = readClientInfoRequests(raw);
  if (read.kind !== 'row') return undefined;
  const withdrawn = read.row.requests.filter((r) => r.requestedAt !== undefined && r.withdrawnAt !== undefined);
  if (withdrawn.length === 0) return [];
  const uploads = groupOwnUploads(ownDocuments);
  const answers = answersForRequests(detailsAttributes, withdrawn.filter((r) => r.kind === 'question').map((r) => r.id));
  const out: WithdrawnInfoRequestForClient[] = [];
  for (const r of withdrawn) {
    // An account-number request holds nothing of the client's once withdrawn: the façade deletes
    // its slot, and sealed number and all, BEFORE it withdraws the request (docs/38 §4.6), and the
    // kind takes no file. So it is never listed here, whatever sits under its id.
    const holds = r.kind === 'question' ? answers.has(r.id) : r.kind === 'account-number' ? false : (uploads.get(r.id)?.length ?? 0) > 0;
    if (!holds) continue;
    out.push({
      id: r.id,
      kind: r.kind,
      ...(r.accountLabel === undefined ? {} : { accountLabel: r.accountLabel }),
      ...(r.prompt === undefined ? {} : { prompt: r.prompt }),
    });
  }
  return out;
}

/**
 * The note of the LATEST send that carries one AND names at least one request in `shownRequestIds`
 * (the ids of the projection the client is shown), as text only. Never the sender's name, the
 * send's kind, its time or its request ids, and never any other send's note. A send stores the
 * ids it covers in `requestIds`, and the note rides the FIRST entry of a send only
 * (`sendEntries`), so for a send of more than {@link MAX_SEND_REQUEST_IDS} requests this rule looks
 * at the first fifty only. "Latest" is the greatest `at` (ties go to the one stored later). A send
 * with no note, or whose requests have all been withdrawn, is skipped, so a note about requests
 * that are gone is not shown beside the ones that remain. `undefined` for a row that does not
 * parse or has no such note.
 */
export function latestSendNoteForClient(raw: unknown, shownRequestIds: Iterable<string>): string | undefined {
  const read = readClientInfoRequests(raw);
  if (read.kind !== 'row') return undefined;
  const shown = new Set(shownRequestIds);
  let best: { at: number; note: string } | undefined;
  for (const send of read.row.sends) {
    if (send.note === undefined || !send.requestIds.some((id) => shown.has(id))) continue;
    const at = Date.parse(send.at);
    if (best === undefined || at >= best.at) best = { at, note: send.note };
  }
  return best?.note;
}

// ── The client's typed answers ────────────────────────────────────────────────────────────

/** The two parallel lists the `requests` form section stores (`ONBOARDING_FORM_FIELDS.requests`). */
export const INFO_ANSWER_IDS_KEY = 'requests_answerIds';
export const INFO_ANSWER_TEXTS_KEY = 'requests_answerTexts';

/**
 * The text of a typed answer as it is stored: {@link cleanInfoText} (zero-width characters
 * removed, whitespace collapsed so a paragraph break becomes a space, and the digits of any run of
 * nine or more hidden as `#`), clipped to {@link INFO_ANSWER_MAX}. The same rule the staff side
 * applies to free text, so an account number typed into an answer is not kept.
 */
export function cleanInfoAnswer(raw: string): string {
  return cleanInfoText(raw, INFO_ANSWER_MAX);
}

/**
 * The typed answers in an `onboarding-details` attribute bag, by request id, for the requests in
 * `requestIds` ONLY. Pairs the two lists by index; an answer whose id is not in `requestIds` is
 * ignored (what counts as a wanted id is the CALLER's choice: this function knows nothing of
 * drafts or withdrawal, and the staff read passes every sent question, withdrawn ones included), as is an
 * entry whose text is not a string or is blank once cleaned. The first answer for an id wins. The
 * text is cleaned again here ({@link cleanInfoAnswer}) because the stored lists are written by the
 * client's browser.
 */
export function answersForRequests(attributes: unknown, requestIds: Iterable<string>): Map<string, string> {
  const wanted = new Set(requestIds);
  const out = new Map<string, string>();
  if (typeof attributes !== 'object' || attributes === null) return out;
  const bag = attributes as Record<string, unknown>;
  const ids = bag[INFO_ANSWER_IDS_KEY];
  const texts = bag[INFO_ANSWER_TEXTS_KEY];
  if (!Array.isArray(ids) || !Array.isArray(texts)) return out;
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    const text = texts[i];
    if (typeof id !== 'string' || typeof text !== 'string' || !wanted.has(id) || out.has(id)) continue;
    const cleaned = cleanInfoAnswer(text);
    if (cleaned !== '') out.set(id, cleaned);
  }
  return out;
}

// ── Status ────────────────────────────────────────────────────────────────────────────────

export type InfoRequestStatus = 'withdrawn' | 'answered' | 'open';

/**
 * What exists today that could answer a request. Explicit inputs, so the function is pure and the
 * caller says plainly what it could and could not look at.
 *
 * `statementAddedAts`: when each statement tied to the account was added. The Requests tab passes
 * the ones that carry a date (a statement with no date cannot be compared and is left out) and,
 * since the client slice (crossmarc/steward-app#2284), the time of each file the CLIENT added under
 * this very request. `documentOnFile`: the client has added a file under this document request.
 * `answered`: the client has typed an answer to this question request. The two flags are set by
 * the façade's `clientProvided` read (`handlers/clientRequests.ts`), which matches on the client
 * and the request id together; nothing in this function looks at either.
 *
 * A statement request is answered by a statement added at or after the moment it was SENT
 * (`requestedAt`), so one already on file when the request went out does not answer it. A draft
 * has no `requestedAt` and so is never answered. The tab still OFFERS the request while a
 * statement is on file: there is no recency rule yet for "this one is too old".
 *
 * `accountNumberFilled` (docs/38, crossmarc/steward-app#2298): appcore holds a number in this
 * request's slot, whoever entered it. It is the ONLY thing that answers an `account-number`
 * request: a file or typed text never does. Optional, and absent reads as false, so a caller that
 * has not read the slots (the Requests tab, until its own slice passes it) shows the request open.
 */
export interface InfoRequestEvidence {
  statementAddedAts: readonly string[];
  documentOnFile: boolean;
  answered: boolean;
  accountNumberFilled?: boolean;
}

/** `withdrawn` first (a withdrawn request is not asked, whatever arrived), then `answered` by
 *  the evidence for the request's own kind, otherwise `open`. */
export function deriveInfoRequestStatus(
  request: Pick<InfoRequest, 'kind' | 'withdrawnAt' | 'requestedAt'>,
  evidence: InfoRequestEvidence,
): InfoRequestStatus {
  if (request.withdrawnAt !== undefined) return 'withdrawn';
  if (request.kind === 'statement' && request.requestedAt !== undefined) {
    const sent = Date.parse(request.requestedAt);
    if (evidence.statementAddedAts.some((at) => Date.parse(at) >= sent)) return 'answered';
  }
  if (request.kind === 'document' && evidence.documentOnFile) return 'answered';
  if (request.kind === 'question' && evidence.answered) return 'answered';
  if (request.kind === 'account-number' && evidence.accountNumberFilled === true) return 'answered';
  return 'open';
}

/** True while the request has not been sent (a draft). A staff-only request is never sent, so
 *  this is ALSO true of one that is not withdrawn: a caller that means "a draft a send would
 *  send, and a draft save may replace" wants {@link isOutgoingInfoRequestDraft}. */
export function isInfoRequestDraft(request: Pick<InfoRequest, 'requestedAt' | 'withdrawnAt'>): boolean {
  return request.requestedAt === undefined && request.withdrawnAt === undefined;
}

/**
 * A request the firm keeps for a number it records itself (docs/38 §4.5). The stored schema
 * ({@link InfoRequestSchema}) refuses the flag on any kind but `account-number`, refuses it as
 * anything but `true`, and refuses a row in which such a request carries `requestedAt`. That last
 * rule is what the client-facing reads rest on: {@link projectInfoRequestsForClient} and
 * {@link projectWithdrawnInfoRequestsForClient} show only requests that carry `requestedAt`, so
 * neither names this flag. `clientInfoRequests.test.ts` ("staff-only") pins the three refusals and
 * both projections. Since docs/38 step 6 (crossmarc/steward-app#2298) the projections no longer
 * drop the `account-number` kind, so that refusal is the ONLY thing that keeps a staff-only
 * request from the client; the projection tests also feed each projection a staff-only request
 * directly (a row the schema would refuse is unreadable, which is what they pin). The refusal
 * test lists the readers that rest on it.
 *
 * NOT enforced by the schema, which sees one row at a time: that the flag is never REMOVED from a
 * request that had it. The façade's writes are what keep it (`handlers/clientInfoRequests.ts`,
 * "Staff-only requests").
 */
export function isStaffOnlyInfoRequest(request: Pick<InfoRequest, 'staffOnly'>): boolean {
  return request.staffOnly === true;
}

/** A draft that a send would send and a draft save may replace or drop: not sent, not withdrawn,
 *  and not staff only. */
export function isOutgoingInfoRequestDraft(request: Pick<InfoRequest, 'requestedAt' | 'withdrawnAt' | 'staffOnly'>): boolean {
  return isInfoRequestDraft(request) && !isStaffOnlyInfoRequest(request);
}
